import { useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useWebShare from 'react-use-web-share'
import usePrev from '../../../hooks/usePrev'
import { FormattedMessage } from 'react-intl'
import mapValues from 'lodash/mapValues'
import { sendInvite } from '../../../actions/invite'
import { flashNotification } from '../../../actions'
import { SHARE } from '../../../constants/messages'
import { selectClientLegacyId } from '../../../store/selectors/client'
import { selectInviteSending } from '../../../store/selectors/invite'
import { Button, FormField, Text, If, Unless, Page } from '../..'
import { SlideOnMount } from '../../shared/Animations'
import { IconFacebook, IconLink, IconExternalLink } from '../../shared/Icon'
import { FIRST_NAME, LAST_NAME, EMAIL } from '../../shared/FormField/names'
import { TrackingEventConstants } from '../../../constants/tracking'
import { useTrackingContext } from '../../../providers/tracking'
import envelopeImg from './envelope.png'
import shareImg from './share.gif'
import { useLocation } from 'react-router-dom'
import {
  selectCustomerBuyerShareUri,
  selectCustomerId,
  selectCustomerLegacyRef
} from '../../../store/selectors/customerProfile'
import { useCreateHomeClientEvent } from '../../../hooks/useCreateHomeClientEvent'
import { ClientEventName } from '../../../api/gqlaxy/gql/generated/graphql'

import styles from './SharePanel.module.scss'

const fieldMap = {
  firstName: FIRST_NAME,
  lastName: LAST_NAME,
  email: EMAIL
}

const SharePanel = () => {
  const { trackingClient } = useTrackingContext()
  const dispatch = useDispatch()
  const { loading, isSupported, share } = useWebShare()
  const location = useLocation()

  const formRef = useRef()
  const sending = useSelector(selectInviteSending)
  const didSend = usePrev(sending)

  const createReferralInviteClientEvent = useCreateHomeClientEvent({ eventName: ClientEventName.ReferralInviteSent })

  const clientLegacyRef = useSelector(selectClientLegacyId)
  const customerProfileId = useSelector(selectCustomerId)
  const buyerShareUri = useSelector(selectCustomerBuyerShareUri)
  const agentId = useSelector(selectCustomerLegacyRef)

  const purlUrl = process.env.REACT_APP_PURL_URL
  const showBuyerLink = buyerShareUri && location.pathname.includes('buy')
  const shareLink = showBuyerLink
    ? `${purlUrl}/buyer?inviterId=${clientLegacyRef}&agentId=${agentId}&id=${customerProfileId}&type=valueMarket&ref=client_referral&utm_campaign=client_invite&utm_content=copy_invite_link&utm_medium=share_link&utm_source=purl`
    : `${purlUrl}?inviterId=${clientLegacyRef}&agentId=${agentId}&id=${customerProfileId}&type=valueMarket&ref=client_referral&utm_campaign=client_invite&utm_content=copy_invite_link&utm_medium=share_link&utm_source=purl`

  const appId = process.env.REACT_APP_FACEBOOK_APP_ID

  useEffect(() => {
    if (didSend && !sending && formRef.current) {
      Object.keys(fieldMap).forEach(k => (formRef.current.elements[k].value = ''))
    }
  }, [didSend, sending])

  const copyInviteLink = () => {
    const tempInput = document.createElement('input')
    tempInput.style.cssText = 'position: absolute; left: -1000px; top: -1000px;'
    tempInput.value = shareLink

    document.body.appendChild(tempInput)

    tempInput.select()

    document.execCommand('copy')
    document.body.removeChild(tempInput)
  }

  const renderMobileShare = !loading && isSupported

  return (
    <Page>
      <Page.Body>
        <SlideOnMount>
          <img className={styles.parachute} src={shareImg} alt='house' />
          <Text element='div' className={styles.header} variant='neutralCool' variantCount='900'>
            <FormattedMessage id='SharePanel.header' defaultMessage='Sharing is caring' />
          </Text>
          <Text element='p' className={styles.subheader} variant='neutralCool' variantCount='700'>
            <FormattedMessage
              id='SharePanel.subheader'
              defaultMessage='Connect your friends and family to their own Homebot report so they can understand their financial situation and see custom options to better manage their wealth.'
            />
          </Text>
        </SlideOnMount>

        <SlideOnMount>
          <If condition={renderMobileShare}>
            <Button
              data-qa='sharePanelMobileShareButton'
              onClick={() => {
                trackingClient?.homebotPanelShared({
                  share_method: TrackingEventConstants.SHARE_VIA_MOBILESHARE
                })
                const shareObj = {
                  title: 'Share Homebot',
                  url: shareLink
                }
                share(shareObj)
              }}
              className={styles.shareButton}
              iconStart={IconExternalLink}
            >
              <FormattedMessage id='SharePanel.mobileShare' defaultMessage='Share' />
            </Button>
          </If>

          <Unless condition={renderMobileShare}>
            <Button
              data-qa='sharePanelCopyLinkButton'
              onClick={() => {
                copyInviteLink()
                trackingClient?.homebotPanelShared({
                  share_method: TrackingEventConstants.COPY_INVITE_LINK
                })
                dispatch(
                  flashNotification({
                    message: SHARE.inviteLinkCopied,
                    messageType: 'success'
                  })
                )
              }}
              className={styles.shareButton}
              iconStart={IconLink}
            >
              <FormattedMessage id='SharePanel.copyInviteLink' defaultMessage='Copy invite link' />
            </Button>

            <Button
              data-qa='sharePanelFacebookButton'
              onClick={() => {
                trackingClient?.homebotPanelShared({
                  share_method: TrackingEventConstants.SHARE_ON_FACEBOOK
                })
                window.open(
                  `https://www.facebook.com/dialog/share?app_id=${appId}&href=${encodeURIComponent(shareLink)}`,
                  'pop',
                  'width=750,height=750'
                )
              }}
              className={styles.shareButton}
              iconStart={IconFacebook}
            >
              <FormattedMessage id='SharePanel.shareOnFacebook' defaultMessage='Share on Facebook' />
            </Button>
          </Unless>
        </SlideOnMount>

        <SlideOnMount>
          <img className={styles.envelope} src={envelopeImg} alt='envelope' />
          <div className={styles.formContainer}>
            <Text element='div' className={styles.emailInviteFormTitle} variant='neutralCool' variantCount='900'>
              <FormattedMessage id='SharePanel.emailInvite' defaultMessage='Send an email invite' />
            </Text>
            <form
              className={styles.form}
              ref={formRef}
              onSubmit={e => {
                e.preventDefault()
                trackingClient?.homebotPanelShared({
                  share_method: TrackingEventConstants.SEND_EMAIL_INVITE
                })
                const data = mapValues(fieldMap, k => e.target.elements[k].value)
                dispatch(sendInvite(data, createReferralInviteClientEvent))
              }}
            >
              <div className={styles.stackable}>
                <FormField.FirstName required disabled={sending} />
                <FormField.LastName required disabled={sending} />
              </div>
              <FormField.Email required disabled={sending} />

              <Text element='div' align='center' variant='neutralCool' variantCount='900'>
                <Button type='submit' loading={sending}>
                  <FormattedMessage id='Homeowner.shareForm.submitButton' defaultMessage='Send Invite' />
                </Button>
              </Text>
            </form>
          </div>
        </SlideOnMount>
      </Page.Body>
    </Page>
  )
}

export default SharePanel
